import React, { useState } from "react";
import { Button } from "./ui/button";
import { Calculator, Home, Euro, Paintbrush, Droplets, Shield, Receipt, ArrowRight, MessageCircle } from "lucide-react";

const PriceCalculatorSection = () => {
  const [selectedSize, setSelectedSize] = useState("");
  const [showResult, setShowResult] = useState(false);

  const priceData = [
    { sqm: "30", price: 490, label: "30m² Wohnung Pauschalpreis" },
    { sqm: "40", price: 679, label: "40m² Wohnung" },
    { sqm: "50", price: 890, label: "50m² Wohnung" },
    { sqm: "60", price: 990, label: "60m² Wohnung" },
    { sqm: "70", price: 1190, label: "70m² Wohnung" },
    { sqm: "80", price: 1290, label: "80m² Wohnung" },
    { sqm: "90", price: 1390, label: "90m² Wohnung" },
    { sqm: "100", price: 1490, label: "100m² Wohnung" }
  ];

  const handleSizeSelect = (sizeData) => {
    setSelectedSize(sizeData);
    setShowResult(true);
  };

  const calculateTotal = (netPrice) => {
    const vat = netPrice * 0.19;
    const total = netPrice + vat;
    return { net: netPrice, vat: vat, total: total };
  };

  const handleWhatsAppContact = () => {
    if (!selectedSize) return;
    
    const calculation = calculateTotal(selectedSize.price);
    const message = `Hallo, ich interessiere mich für einen Festpreis für Malerarbeiten.

🏠 Wohnungsgröße: ${selectedSize.label}
💰 Festpreis NETTO: ${selectedSize.price}€
💰 Festpreis BRUTTO: ${calculation.total.toFixed(2)}€ (inkl. 19% MwSt.)

⚠️ WICHTIG: Dieser Preis gilt nur für leere Wohnungen ohne Möbel.

Bitte kontaktieren Sie mich für weitere Details.
Vielen Dank!`;

    const whatsappUrl = `https://wa.me/4915758183212?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const resetCalculator = () => {
    setSelectedSize("");
    setShowResult(false);
  };

  return (
    <section id="preiskalkulator" className="py-20 bg-gradient-to-br from-orange-50 via-blue-50 to-orange-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-orange-500 to-blue-600 rounded-full mb-6 shadow-2xl">
            <Calculator className="text-white" size={40} />
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            <span className="text-orange-600">Festpreis</span>{" "}
            <span className="text-blue-600">Kalkulator</span>
          </h2>
          <p className="text-lg sm:text-xl lg:text-2xl text-gray-700 mb-8 max-w-4xl mx-auto leading-relaxed">
            Transparente Festpreise für Ihre Wohnung in Baden-Württemberg
          </p>
          <div className="w-32 h-1 bg-gradient-to-r from-orange-500 to-blue-600 mx-auto rounded-full shadow-lg"></div>
        </div>

        <div className="max-w-6xl mx-auto">
          {!showResult ? (
            /* Price Selection */
            <div className="bg-white rounded-3xl shadow-2xl p-8 border border-gray-200">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  Wählen Sie Ihre Wohnungsgröße
                </h2>
                <p className="text-lg text-gray-600">
                  Alle Preise sind Festpreise - keine versteckten Kosten!
                </p>
              </div>

              {/* Service Info */}
              <div className="bg-orange-100 p-6 rounded-2xl mb-8">
                <h4 className="font-bold text-orange-800 mb-4 text-xl flex items-center gap-2">
                  <Shield className="text-orange-600" size={24} />
                  Leistungsumfang:
                </h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3">
                    <Paintbrush className="text-orange-600" size={20} />
                    <span className="text-orange-700">Professionelle Malerarbeiten bis 2,60m Deckenhöhe</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Droplets className="text-blue-600" size={20} />
                    <span className="text-orange-700">Hochwertige PROFITEC Farbe inklusive</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Shield className="text-green-600" size={20} />
                    <span className="text-orange-700">Alle Abdeckarbeiten inklusive</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Receipt className="text-purple-600" size={20} />
                    <span className="text-orange-700">Preise sind NETTO zzgl. MwSt.</span>
                  </div>
                </div>
                <div className="mt-4 p-3 bg-red-100 border border-red-300 rounded-lg">
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 bg-red-500 rounded-full flex items-center justify-center">
                      <span className="text-white text-xs font-bold">!</span>
                    </div>
                    <span className="text-red-800 font-semibold text-sm">
                      WICHTIG: Preise gelten nur für leere Wohnungen ohne Möbel
                    </span>
                  </div>
                </div>
              </div>

              {/* Price Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                {priceData.map((item) => (
                  <button
                    key={item.sqm}
                    onClick={() => handleSizeSelect(item)}
                    className="group relative bg-gradient-to-br from-white to-gray-50 rounded-2xl p-4 sm:p-6 border-2 border-gray-200 hover:border-orange-400 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 sm:hover:-translate-y-2 text-left min-h-[140px] sm:min-h-[160px]"
                  >
                    <div className="flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-orange-500 to-blue-600 rounded-xl mb-3 sm:mb-4 group-hover:scale-110 transition-transform duration-300">
                      <Home className="text-white" size={20} />
                    </div>
                    <h3 className="font-bold text-base sm:text-lg text-gray-900 mb-2">
                      {item.sqm}m² Wohnung
                    </h3>
                    <div className="space-y-1">
                      <div className="text-xl sm:text-2xl font-bold text-orange-600">
                        {item.price}€
                      </div>
                      <div className="text-xs sm:text-sm text-gray-500">NETTO zzgl. MwSt.</div>
                      <div className="text-base sm:text-lg font-semibold text-blue-600">
                        {calculateTotal(item.price).total.toFixed(0)}€ inkl.
                      </div>
                    </div>
                    <ArrowRight className="absolute bottom-3 right-3 sm:bottom-4 sm:right-4 text-orange-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300" size={16} />
                  </button>
                ))}
              </div>
            </div>
          ) : (
            /* Result Display */
            <div className="bg-white rounded-3xl shadow-2xl p-8 border border-gray-200">
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-green-500 to-green-600 rounded-full mb-4">
                  <Euro className="text-white" size={32} />
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-2">
                  Ihr Festpreis für {selectedSize.label}
                </h2>
                <p className="text-lg text-gray-600">
                  Transparente Preisgestaltung - keine versteckten Kosten
                </p>
              </div>

              {/* Price Breakdown */}
              <div className="max-w-2xl mx-auto">
                <div className="bg-gradient-to-br from-orange-50 to-blue-50 rounded-2xl p-8 mb-8">
                  <div className="space-y-4">
                    <div className="flex justify-between items-center text-lg">
                      <span className="text-gray-700">Malerarbeiten (NETTO):</span>
                      <span className="font-bold text-gray-900">{selectedSize.price}€</span>
                    </div>
                    <div className="flex justify-between items-center text-lg">
                      <span className="text-gray-700">MwSt. (19%):</span>
                      <span className="font-bold text-gray-900">{calculateTotal(selectedSize.price).vat.toFixed(2)}€</span>
                    </div>
                    <div className="border-t-2 border-gray-300 pt-4">
                      <div className="flex justify-between items-center text-2xl">
                        <span className="font-bold text-gray-900">Gesamtpreis:</span>
                        <span className="font-bold text-orange-600">{calculateTotal(selectedSize.price).total.toFixed(2)}€</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Service Details */}
                <div className="bg-blue-50 rounded-2xl p-6 mb-8">
                  <h3 className="font-bold text-blue-800 mb-4 text-lg">Im Preis enthalten:</h3>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <Droplets className="text-blue-500" size={20} />
                      <span className="text-blue-700">Inkl. PROFITEC Farbe</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Home className="text-green-500" size={20} />
                      <span className="text-blue-700">Bis 2,60m Deckenhöhe</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Shield className="text-orange-500" size={20} />
                      <span className="text-blue-700">Alle Abdeckarbeiten inklusive</span>
                    </div>
                  </div>
                  <div className="mt-4 p-3 bg-red-100 border border-red-300 rounded-lg">
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 bg-red-500 rounded-full flex items-center justify-center">
                        <span className="text-white text-xs font-bold">!</span>
                      </div>
                      <span className="text-red-800 font-semibold text-sm">
                        WICHTIG: Preis gilt nur für leere Wohnung ohne Möbel
                      </span>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button 
                    onClick={handleWhatsAppContact}
                    className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white px-8 py-4 text-lg font-bold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-3"
                  >
                    <MessageCircle size={24} />
                    Kostenlos anfragen
                  </Button>
                  <Button 
                    onClick={resetCalculator}
                    variant="outline"
                    className="border-2 border-gray-300 text-gray-700 hover:bg-gray-50 px-8 py-4 text-lg font-bold rounded-xl"
                  >
                    Andere Größe wählen
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Trust Indicators */}
        <div className="mt-16 grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
          <div className="text-center">
            <div className="w-16 h-16 bg-gradient-to-r from-orange-500 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="text-white" size={32} />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Festpreisgarantie</h3>
            <p className="text-gray-600">
              Transparente Preise ohne versteckte Kosten
            </p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <Paintbrush className="text-white" size={32} />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Profi-Qualität</h3>
            <p className="text-gray-600">
              Erfahrene Maler mit hochwertigen Materialien
            </p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <MessageCircle className="text-white" size={32} />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Schnelle Beratung</h3>
            <p className="text-gray-600">
              Direkte WhatsApp-Beratung und Terminvereinbarung
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PriceCalculatorSection;